import { useEffect, useState } from 'react'
import { ferry, clock, type Position, type Sailing } from '../lib/ferry'

/**
 * Where the boat is.
 *
 * ── Why this is an SVG and not a map library ─────────────────────────────────
 *
 * There are exactly four routes and they are all the same channel. A tile map
 * would add a dependency, a network round trip and a licence, to render an
 * ocean — and it would answer a question nobody has ("what is the coastline
 * like?") while answering the real one ("how far along is it?") no better.
 *
 * A line between two named points, with the vessel on it, is the whole product.
 */
export default function RouteMap({ sailing }: { sailing: Sailing }) {
  const [position, setPosition] = useState<Position | null>(null)

  useEffect(() => {
    let live = true
    const tick = () => { void ferry.position(sailing.id).then((d) => { if (live) setPosition(d.position) }).catch(() => {}) }
    tick()
    // Underway, a minute is plenty — the crossing is under an hour and the boat
    // does not turn sharply.
    const timer = window.setInterval(tick, 60_000)
    return () => { live = false; window.clearInterval(timer) }
  }, [sailing.id])

  const [fromName, toName] = sailing.route.split('-').map((s) => s[0]!.toUpperCase() + s.slice(1))

  /*
   * Progress: measured if the vessel is reporting, otherwise inferred from the
   * clock. The inferred one is clearly labelled — a guess presented as a
   * measurement is how people stop trusting the whole screen.
   */
  const measured = position?.progress != null
  const elapsed = Date.now() - (sailing.actual_departure ?? sailing.scheduled_departure)
  const total = sailing.scheduled_arrival - sailing.scheduled_departure
  const inferred = Math.max(0, Math.min(1, elapsed / Math.max(total, 1)))
  const progress = measured ? (position!.progress as number) : inferred
  const underway = sailing.status === 'departed'

  return (
    <div className="routemap">
      <svg viewBox="0 0 320 90" role="img" aria-label={`${fromName} to ${toName}`}>
        <line x1="30" y1="52" x2="290" y2="52" className="track" />
        <line x1="30" y1="52" x2={30 + 260 * progress} y2="52" className="track-done" />
        <circle cx="30" cy="52" r="6" className="port" />
        <circle cx="290" cy="52" r="6" className="port" />
        <text x="30" y="76" textAnchor="middle" className="port-label">{fromName}</text>
        <text x="290" y="76" textAnchor="middle" className="port-label">{toName}</text>

        {underway && (
          <g transform={`translate(${30 + 260 * progress}, 52)`} className={measured ? 'boat' : 'boat boat-inferred'}>
            {/* A hull, pointing the way it is going. */}
            <path d="M -11 -5 L 8 -5 L 13 0 L 8 5 L -11 5 Z" />
            <circle cx="0" cy="0" r="14" className="ping" />
          </g>
        )}
      </svg>

      <p className="muted small">
        {sailing.status === 'arrived' ? `Arrived ${clock(sailing.scheduled_arrival)}`
          : underway ? (
            measured
              ? `Underway · ${Math.round(progress * 100)}% across${position?.speed_knots ? ` · ${Math.round(position.speed_knots)} kn` : ''}`
              : `Underway · about ${Math.round(progress * 100)}% by the clock — no live position`
          )
          : sailing.status === 'boarding' ? 'Boarding now'
          : sailing.status === 'cancelled' ? 'Cancelled'
          : `Departs ${clock(sailing.scheduled_departure)}`}
      </p>
    </div>
  )
}
